/**
 * Enlaces de WhatsApp (F3-I) — `wa.me` con plantilla interpolada.
 *
 * v1 SIN Business API: un enlace `https://wa.me/<E164>?text=<mensaje>` abre el chat
 * con el mensaje pre-cargado. Se usa en: tarjetas kanban de leads (acción rápida),
 * timeline del lead (registrar evento `whatsapp` al pulsar) y filas de facturas
 * vencidas ("Cobrar por WhatsApp" con variables nombre/monto).
 *
 * La normalización de teléfono es Venezuela-first y DEBE coincidir con
 * `packages/core-rust/src/crm/phone.rs` (mismo comportamiento en cliente y server).
 */

/** Variables de interpolación (p.ej. `{ nombre: "Ada", curso: "Python", monto: "$50" }`). */
export type WaVars = Record<string, string | number | null | undefined>;

/**
 * Normaliza un teléfono a E.164 (`+` + 8–15 dígitos). País por defecto: Venezuela.
 * Devuelve `null` si no se puede formar un E.164 plausible.
 */
export function normalizePhoneVE(input: string): string | null {
  const trimmed = (input ?? "").trim();
  if (!trimmed) {
    return null;
  }
  const hadPlus = trimmed.startsWith("+");
  const digits = trimmed.replaceAll(/\D/g, "");
  if (!digits) {
    return null;
  }

  let e164: string;
  if (hadPlus) {
    e164 = `+${digits}`;
  } else if (digits.startsWith("58") && digits.length - 2 >= 9) {
    e164 = `+58${digits.slice(2)}`;
  } else if (digits.startsWith("0") && digits.length === 11) {
    e164 = `+58${digits.slice(1)}`;
  } else if (digits.length === 10 && (digits.startsWith("4") || digits.startsWith("2"))) {
    e164 = `+58${digits}`;
  } else {
    e164 = `+${digits}`;
  }

  const count = e164.replaceAll(/\D/g, "").length;
  return count >= 8 && count <= 15 ? e164 : null;
}

/**
 * Interpola `{{clave}}` en una plantilla con `vars`. Las claves ausentes se
 * sustituyen por cadena vacía (nunca deja `{{...}}` a la vista). No introduce
 * HTML (el texto va URL-encoded al abrir wa.me).
 */
export function interpolateWaTemplate(template: string, vars: WaVars = {}): string {
  return (template ?? "").replaceAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_m, key: string) => {
    const v = vars[key];
    return v === null || v === undefined ? "" : String(v);
  });
}

/**
 * Construye un enlace `wa.me`. Devuelve `null` si el teléfono no es válido.
 * `templateBody` puede contener variables `{{nombre}}`, `{{curso}}`, `{{monto}}`…
 */
export function buildWaLink(phone: string, templateBody: string, vars: WaVars = {}): string | null {
  const e164 = normalizePhoneVE(phone);
  if (!e164) {
    return null;
  }
  const number = e164.replace(/^\+/, "");
  const text = interpolateWaTemplate(templateBody, vars);
  const query = text ? `?text=${encodeURIComponent(text)}` : "";
  return `https://wa.me/${number}${query}`;
}
